/**
 * Iter-60 — Responsive section accordion.
 *
 * Renders a "brut-card" section that:
 *   * On desktop (≥ md) is always expanded — matches the pre-Iter-60
 *     Landing-Page Editor exactly, so desktop workflows regress zero.
 *   * On mobile becomes a collapsible accordion header + panel.
 *
 * Body children stay mounted so form state is never lost when a section
 * closes and reopens. This is critical for the editor where the user
 * may fill in fields across multiple sections before saving.
 */
import React, { useState } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function ResponsiveSectionAccordion({
  title,
  summary,
  defaultOpen = true,
  testId,
  children,
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(defaultOpen);
  const effectivelyOpen = isMobile ? open : true;
  const tid = testId || `section-${(title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="brut-card mb-6" data-testid={tid}>
      <button
        type="button"
        onClick={() => isMobile && setOpen((v) => !v)}
        aria-expanded={effectivelyOpen}
        className={`w-full flex items-center gap-2 border-b border-black px-4 md:px-5 py-3 text-left ${
          isMobile ? "cursor-pointer" : "cursor-default"
        }`}
        data-testid={`${tid}-header`}
      >
        <span className="font-chivo font-black uppercase tracking-tighter flex-1">
          {title}
        </span>
        {summary && (
          <span className="hidden md:inline label-mono">{summary}</span>
        )}
        {isMobile &&
          (open ? (
            <CaretDown size={16} weight="bold" />
          ) : (
            <CaretRight size={16} weight="bold" />
          ))}
      </button>
      {/* Keep body mounted so form state never resets on collapse. */}
      <div
        className={`${effectivelyOpen ? "block" : "hidden"} p-4 md:p-5 space-y-4`}
        data-testid={`${tid}-body`}
      >
        {children}
      </div>
    </div>
  );
}
